import React, { useEffect, useState } from "react";
import { X, CheckCircle, AlertCircle, Loader } from "lucide-react";

const MessageSendingProgress = ({
  isVisible,
  totalMessages,
  sentMessages,
  failedMessages,
  estimatedTimeRemaining,
  onClose,
  status, // 'sending', 'completed', 'failed', 'paused'
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const progressPercentage =
    totalMessages > 0 ? Math.round((sentMessages / totalMessages) * 100) : 0;

  // Debug log
  console.log("🎨 MessageSendingProgress render:", {
    isVisible,
    totalMessages,
    sentMessages,
    failedMessages,
    status,
    progressPercentage,
    estimatedTimeRemaining,
  });

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return "Calculating...";

    if (seconds < 60) {
      return `${Math.round(seconds)} seconds`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${minutes}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed ${
        isMinimized ? "bottom-4 right-4" : "bottom-4 right-4"
      } z-50 transition-all duration-300`}
    >
      <div
        className={`bg-white rounded-lg shadow-2xl border border-gray-200 ${
          isMinimized ? "w-72" : "w-96"
        } overflow-hidden`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {status === "sending" && (
              <Loader className="w-5 h-5 text-white animate-spin" />
            )}
            {status === "completed" && (
              <CheckCircle className="w-5 h-5 text-green-300" />
            )}
            {status === "failed" && (
              <AlertCircle className="w-5 h-5 text-red-300" />
            )}
            <h3 className="text-white font-semibold">
              {status === "sending" && "Sending Messages..."}
              {status === "completed" && "Messages Sent Successfully"}
              {status === "failed" && "Message Sending Failed"}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="text-white hover:bg-white/20 rounded p-1 transition"
              title={isMinimized ? "Expand" : "Minimize"}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={isMinimized ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                />
              </svg>
            </button>
            <button
              onClick={() => {
                console.log("Close button clicked");
                if (onClose) {
                  onClose();
                }
              }}
              className="text-white hover:bg-white/20 rounded p-1 transition"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="p-4 space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 font-medium">Progress</span>
                <span className="text-gray-900 font-bold">
                  {progressPercentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    status === "completed"
                      ? "bg-green-500"
                      : status === "failed"
                      ? "bg-red-500"
                      : "bg-gradient-to-r from-blue-500 to-blue-600"
                  }`}
                  style={{ width: `${progressPercentage}%` }}
                >
                  <div className="w-full h-full bg-white/30 animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-blue-600 font-medium mb-1">
                  Total Messages
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {totalMessages}
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <p className="text-xs text-green-600 font-medium mb-1">Sent</p>
                <p className="text-2xl font-bold text-green-900">
                  {sentMessages}
                </p>
              </div>

              <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                <p className="text-xs text-orange-600 font-medium mb-1">
                  Remaining
                </p>
                <p className="text-2xl font-bold text-orange-900">
                  {totalMessages - sentMessages}
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-xs text-red-600 font-medium mb-1">Failed</p>
                <p className="text-2xl font-bold text-red-900">
                  {failedMessages}
                </p>
              </div>
            </div>

            {/* Time Estimate */}
            {status === "sending" && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-600">
                      Estimated Time
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">
                    {estimatedTimeRemaining > 0
                      ? formatTime(estimatedTimeRemaining)
                      : "Calculating..."}
                  </span>
                </div>
              </div>
            )}

            {/* Info Message */}
            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <span className="font-semibold">💡 Tip:</span> You can navigate
                to other pages while messages are being sent in the background.
              </p>
            </div> */}

            {/* Status Messages */}
            {status === "completed" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800 font-medium">
                  ✅ All messages have been sent successfully!
                </p>
              </div>
            )}

            {status === "failed" && failedMessages > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ {failedMessages} message{failedMessages > 1 ? "s" : ""}{" "}
                  failed to send. Check message history for details.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Minimized View */}
        {isMinimized && (
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {sentMessages}/{totalMessages}
              </span>
              <span className="text-sm font-bold text-blue-600">
                {progressPercentage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageSendingProgress;
